import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { onAuth, signOut as fbSignOut } from './lib/auth';

interface AuthState {
  user: User | null;
  ready: boolean;
  /** demo mode = browsing the bundled dataset without signing in */
  demo: boolean;
  enterDemo: () => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState>({
  user: null, ready: false, demo: false, enterDemo: () => {}, signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [demo, setDemo] = useState(false);

  useEffect(() => onAuth((u) => { setUser(u); setReady(true); if (u) setDemo(false); }), []);

  const value: AuthState = {
    user, ready, demo,
    enterDemo: () => {
      setDemo(true);
      // Demo login opts into synthetic data via the URL gate (?demo=1) used across the data layer.
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('demo')) {
          url.searchParams.set('demo', '1');
          window.history.replaceState(null, '', url);
        }
      }
    },
    signOut: async () => { setDemo(false); await fbSignOut(); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
